import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MapPin, Star, StickyNote, Trash2, Check, Loader2, Compass, Share2, Copy, X, Maximize2, Minimize2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { WorldMap } from "@/components/WorldMap";
import { useAuth } from "@/context/AuthContext";
import { phaseBadge } from "@/lib/phases";

const STATUS = { wishlist: { label: "Want to go", color: "#D97706" }, visited: { label: "Visited", color: "#4D7C0F" } };

function FavRow({ fav, onRemove, onSaveNote, onSetStatus }) {
  const [editing, setEditing] = useState(false);
  const [note, setNote] = useState(fav.note || "");
  const [saving, setSaving] = useState(false);
  const st = STATUS[fav.status] || STATUS.wishlist;

  const save = async () => {
    setSaving(true);
    try { await onSaveNote(fav.slug, note); setEditing(false); } finally { setSaving(false); }
  };

  return (
    <li data-testid={`fav-row-${fav.slug}`} className="border-b border-border last:border-b-0 p-3 hover:bg-secondary/40 transition-colors">
      <div className="flex gap-3">
        {fav.image ? (
          <img src={fav.image} alt="" className="h-12 w-12 shrink-0 object-cover border border-border" />
        ) : (
          <span className="h-12 w-12 shrink-0 border border-border" style={{ background: `${st.color}22` }} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Link to={`/pyramid/${fav.slug}`} className="font-serif text-sm font-semibold text-foreground leading-tight hover:text-accent line-clamp-1">
              {fav.name}
            </Link>
            <button data-testid={`fav-remove-${fav.slug}`} onClick={() => onRemove(fav.slug)} title="Remove" className="shrink-0 text-muted-foreground hover:text-destructive transition-colors">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground truncate">{fav.country}</p>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <div className="flex border border-border" data-testid={`fav-status-${fav.slug}`}>
              {["wishlist", "visited"].map((s) => (
                <button
                  key={s}
                  data-testid={`fav-status-${s}-${fav.slug}`}
                  onClick={() => onSetStatus(fav.slug, s)}
                  className="px-2 py-0.5 font-mono text-[8px] uppercase tracking-widest transition-colors border-l first:border-l-0 border-border"
                  style={{ background: fav.status === s ? STATUS[s].color : "transparent", color: fav.status === s ? "#fff" : "hsl(var(--muted-foreground))" }}
                >
                  {STATUS[s].label}
                </button>
              ))}
            </div>
            {!editing && (
              <button data-testid={`fav-note-edit-${fav.slug}`} onClick={() => { setNote(fav.note || ""); setEditing(true); }} className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground hover:text-accent">
                <StickyNote className="h-2.5 w-2.5" /> {fav.note ? "Edit" : "Note"}
              </button>
            )}
          </div>
        </div>
      </div>
      {!editing && fav.note && (
        <p className="mt-2 text-xs text-foreground/90 border-l-2 pl-2 whitespace-pre-wrap" style={{ borderColor: st.color }}>{fav.note}</p>
      )}
      {editing && (
        <div className="mt-2">
          <textarea data-testid={`fav-note-input-${fav.slug}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Private note…" className="w-full bg-background border border-border px-2 py-1.5 text-xs focus:outline-none focus:border-accent resize-y" />
          <div className="mt-1.5 flex items-center gap-2">
            <button data-testid={`fav-note-save-${fav.slug}`} onClick={save} disabled={saving} className="flex items-center gap-1 px-2 py-1 font-mono text-[9px] uppercase tracking-widest bg-foreground text-background hover:bg-foreground/90 disabled:opacity-60">
              {saving ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Check className="h-2.5 w-2.5" />} Save
            </button>
            <button onClick={() => setEditing(false)} className="px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-muted-foreground hover:text-foreground">Cancel</button>
          </div>
        </div>
      )}
    </li>
  );
}

export default function MyMap() {
  const { user, loading, saveNote, toggleFavorite, setStatus } = useAuth();
  const navigate = useNavigate();
  const [favs, setFavs] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [shareUrl, setShareUrl] = useState("");
  const [sharing, setSharing] = useState(false);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("recent");
  const [fullscreen, setFullscreen] = useState(false);

  const load = useCallback(async () => {
    setFetching(true);
    try { const { data } = await api.get("/me/favorites"); setFavs(data || []); }
    catch { setFavs([]); } finally { setFetching(false); }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/account"); return; }
    load();
    api.get("/me/share").then((r) => { if (r.data.token) setShareUrl(`${window.location.origin}/shared/${r.data.token}`); }).catch(() => {});
  }, [user, loading, navigate, load]);

  const remove = async (slug) => {
    await toggleFavorite(slug);
    setFavs((f) => f.filter((x) => x.slug !== slug));
    toast.success("Removed from favorites");
  };
  const onSaveNote = async (slug, note) => {
    await saveNote(slug, note);
    setFavs((f) => f.map((x) => (x.slug === slug ? { ...x, note } : x)));
    toast.success("Note saved");
  };
  const onSetStatus = async (slug, status) => {
    await setStatus(slug, status);
    setFavs((f) => f.map((x) => (x.slug === slug ? { ...x, status } : x)));
  };
  const enableShare = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/me/share");
      const url = `${window.location.origin}/shared/${data.token}`;
      setShareUrl(url);
      try { await navigator.clipboard.writeText(url); toast.success("Share link copied to clipboard"); }
      catch { toast.success("Share link ready"); }
    } catch { toast.error("Could not create share link"); } finally { setSharing(false); }
  };
  const stopShare = async () => {
    try { await api.delete("/me/share"); setShareUrl(""); toast.success("Sharing turned off"); } catch { toast.error("Could not stop sharing"); }
  };
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Copied"); } catch { /* noop */ } };

  const visitedCount = favs.filter((f) => f.status === "visited").length;
  const wishlistCount = favs.length - visitedCount;
  const shownFavs = (filter === "all" ? favs : favs.filter((f) => (filter === "visited" ? f.status === "visited" : f.status !== "visited")))
    .slice()
    .sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "country") return (a.country || "").localeCompare(b.country || "") || a.name.localeCompare(b.name);
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  const points = shownFavs.map((f) => ({ slug: f.slug, name: f.name, country: f.country, lat: f.lat, lng: f.lng, phase: f.phase, area: f.area, image: f.image, note: f.note, status: f.status }));

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-1.5 flex items-center gap-2">
            <Star className="h-3.5 w-3.5" /> Your Collection
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl font-bold text-foreground">My Map</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Every place you've saved, pinned to the globe with your private notes — mark what you've visited and what's still on your list.
          </p>
        </div>
        {favs.length > 0 && (
          <button data-testid="share-map-btn" onClick={enableShare} disabled={sharing} className="flex items-center gap-2 px-4 py-2.5 font-mono text-xs uppercase tracking-widest border border-border text-muted-foreground hover:text-accent hover:border-accent/60 transition-colors disabled:opacity-60">
            {sharing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} Share My Map
          </button>
        )}
      </div>

      {shareUrl && (
        <div data-testid="share-bar" className="mt-4 flex items-center gap-2 flex-wrap border border-accent/40 bg-accent/5 p-3">
          <span className="font-mono text-[10px] uppercase tracking-widest text-accent">Public link</span>
          <input data-testid="share-url" readOnly value={shareUrl} className="flex-1 min-w-[220px] bg-background border border-border px-3 py-1.5 font-mono text-xs text-foreground" />
          <button data-testid="share-copy" onClick={copyLink} className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest bg-foreground text-background hover:bg-foreground/90"><Copy className="h-3 w-3" /> Copy</button>
          <button data-testid="share-stop" onClick={stopShare} className="flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-destructive"><X className="h-3 w-3" /> Stop</button>
        </div>
      )}

      {favs.length > 0 && (
        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <p data-testid="mymap-stats" className="font-mono text-xs uppercase tracking-widest">
            <span style={{ color: STATUS.visited.color }}>{visitedCount} visited</span>
            <span className="mx-2 text-muted-foreground opacity-50">·</span>
            <span style={{ color: STATUS.wishlist.color }}>{wishlistCount} to go</span>
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex border border-border" data-testid="mymap-filter">
              {[
                { k: "all", label: `All · ${favs.length}` },
                { k: "visited", label: `Visited · ${visitedCount}` },
                { k: "wishlist", label: `Want to go · ${wishlistCount}` },
              ].map((o) => (
                <button
                  key={o.k}
                  data-testid={`mymap-filter-${o.k}`}
                  onClick={() => setFilter(o.k)}
                  className="px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors border-l first:border-l-0 border-border"
                  style={{
                    background: filter === o.k ? (o.k === "visited" ? STATUS.visited.color : o.k === "wishlist" ? STATUS.wishlist.color : "hsl(var(--foreground))") : "transparent",
                    color: filter === o.k ? "#fff" : "hsl(var(--muted-foreground))",
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <select
              data-testid="mymap-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-background border border-border px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground focus:outline-none focus:border-accent cursor-pointer"
            >
              <option value="recent">Sort: Date added</option>
              <option value="name">Sort: Name (A–Z)</option>
              <option value="country">Sort: Country</option>
            </select>
            <button
              data-testid="mymap-fullscreen"
              onClick={() => setFullscreen((v) => !v)}
              title={fullscreen ? "Show saved places list" : "Full-screen map"}
              className="flex items-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-widest border border-border text-muted-foreground hover:text-accent hover:border-accent/60 transition-colors"
            >
              {fullscreen ? <><Minimize2 className="h-3.5 w-3.5" /> Show List</> : <><Maximize2 className="h-3.5 w-3.5" /> Full Screen</>}
            </button>
          </div>
        </div>
      )}

      {fetching ? (
        <p className="mt-8 font-mono text-sm text-muted-foreground">Loading your favorites…</p>
      ) : favs.length === 0 ? (
        <div data-testid="mymap-empty" className="mt-10 border border-dashed border-border p-10 text-center">
          <Compass className="mx-auto h-8 w-8 text-accent" />
          <h2 className="mt-4 font-serif text-2xl font-semibold text-foreground">No favorites yet</h2>
          <p className="mt-1 text-sm text-muted-foreground">Browse the archive and hit the ★ on any entry to start building your map.</p>
          <Link to="/archive" className="mt-5 inline-block bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest px-5 py-3 hover:bg-accent/90 transition-colors">Explore the Archive →</Link>
        </div>
      ) : (
        <div className={`mt-6 grid grid-cols-1 gap-6 ${fullscreen ? "" : "lg:grid-cols-3"}`}>
          <div className={fullscreen ? "" : "lg:col-span-2"}>
            <WorldMap points={points} height={fullscreen ? "78vh" : "70vh"} mode="pins" colorBy="status" fitToPoints={true} />
            <div className="mt-2 flex items-center gap-4 font-mono text-[11px] uppercase tracking-widest text-muted-foreground flex-wrap">
              <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {shownFavs.length} shown</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.wishlist.color }} /> Want to go</span>
              <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: STATUS.visited.color }} /> Visited</span>
            </div>
          </div>
          {!fullscreen && (
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Saved places</p>
              <ul className="border border-border max-h-[70vh] overflow-y-auto" data-testid="mymap-list">
                {shownFavs.map((f) => (
                  <FavRow key={f.slug} fav={f} onRemove={remove} onSaveNote={onSaveNote} onSetStatus={onSetStatus} />
                ))}
                {shownFavs.length === 0 && (
                  <li className="p-6 text-center text-sm text-muted-foreground" data-testid="mymap-filter-empty">Nothing here in this view.</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
